import React, { useCallback, useEffect, useRef, useState } from 'react'
import Layout from '../../../components/Layout/Layout'
import { useNavigate, useParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { useAuth } from '../../../context/auth'
import { Modal, Button } from 'antd'
import QtyLog from '../../Admin/PackingList/QtyLog'
import UserPackingListTable from './PackingListTable'
import {
  enqueueScan,
  peekChunk,
  pendingCount,
  pendingOps,
  removeOps,
  requestPersistentStorage,
} from '../../../utils/scanQueue'

// The server accepts up to 200 per request; 100 keeps each chunk small enough
// to retry cheaply on a bad link and well inside express.json()'s 100kb limit.
const SYNC_CHUNK = 100

// A scanned code can be either the item code or the barcode, and the server
// matches them case-insensitively - so the offline lookup has to as well.
const codeMatchesRow = (code, row) => {
  const wanted = String(code || '')
    .trim()
    .toLowerCase()
  if (!wanted) return false
  return (
    String(row.itemCode || '').toLowerCase() === wanted ||
    String(row.barCode || '').toLowerCase() === wanted
  )
}

const UserPackingListDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchData, setSearchData] = useState('')
  const [packingDataList, setPackingDataList] = useState([])
  const [editMode, setEditMode] = useState(null)
  const [editField, setEditField] = useState(null)
  const [filterQuery, setFilterQuery] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const [visibleLogModal, setVisibleLogModal] = useState(false)
  const [currentItemId, setCurrentItemId] = useState(null)
  const [currentItemDetails, setCurrentItemDetails] = useState({})
  const [lastUpdate, setLastUpdate] = useState(new Date())

  const [originalValues, setOriginalValues] = useState({})
  const barcodeInputRef = useRef(null)
  const [focusBarcodeInput, setFocusBarcodeInput] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  // Offline mode is opt-in so the existing online flow is untouched until this
  // has been proven on the floor. It flips on by itself the first time a scan
  // fails with no response, so a link that dies mid-shift cannot silently
  // swallow scans while nobody thinks to switch over.
  const [offlineMode, setOfflineMode] = useState(false)
  const [pending, setPending] = useState(0)
  // The whole packing list for this event, keyed by item code and by barcode.
  // The table only lazy-loads 30 rows at a time, so this is what lets an
  // offline scan be both validated and displayed regardless of which page the
  // item lives on. Null means "we don't know" - validation then stays off
  // rather than guessing.
  const [listSnapshot, setListSnapshot] = useState(null)
  const [isSyncing, setIsSyncing] = useState(false)
  const [storageIsPersistent, setStorageIsPersistent] = useState(true)
  const syncingRef = useRef(false)

  const [auth] = useAuth()
  const userId = auth?.user?._id

  useEffect(() => {
    const shouldFetch = filterQuery.length >= 3 || filterQuery === ''
    if (shouldFetch) {
      fetchData(page)
    }
  }, [page, filterQuery])

  useEffect(() => {
    if (filterQuery.length >= 3 || filterQuery === '') {
      setPage(1)
    }
  }, [filterQuery])

  useEffect(() => {
    barcodeInputRef.current?.focus()
    setFocusBarcodeInput(false)
  }, [focusBarcodeInput])

  // Queued scans have not reached the server, so anything fetched from it is
  // missing them. Layer them back on or a refresh would appear to throw away
  // work the operator has already done.
  const applyPendingToList = async (rows, injectMissing = false) => {
    let ops = []
    try {
      ops = await pendingOps(id)
    } catch (error) {
      console.error('Could not read the scan queue:', error)
    }
    if (ops.length === 0) return rows

    const withDeltas = rows.map((row) => {
      const mine = ops.filter((op) => codeMatchesRow(op.code, row))
      if (mine.length === 0) return row
      return {
        ...row,
        returnQty: (Number(row.returnQty) || 0) + mine.length,
        pendingScans: mine.length,
      }
    })

    if (!injectMissing || !listSnapshot) return withDeltas

    // Scans against items that are not on this page would otherwise disappear
    // from view on a refresh, even though they are still queued. Pull those
    // rows out of the snapshot and show them at the top.
    const alreadyShown = new Set(withDeltas.map((row) => row._id))
    const extras = []

    ops.forEach((op) => {
      const base = listSnapshot.get(String(op.code || '').trim().toLowerCase())
      if (!base || alreadyShown.has(base._id)) return

      const mine = ops.filter((other) => codeMatchesRow(other.code, base))
      extras.push({
        ...base,
        returnQty: (Number(base.returnQty) || 0) + mine.length,
        pendingScans: mine.length,
      })
      alreadyShown.add(base._id)
    })

    return [...extras, ...withDeltas]
  }

  const fetchData = async (newPage = 1) => {
    setIsLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/user/packing-list/details/${id}`,
        {
          params: {
            userId,
            page: newPage,
            limit: 30,
            search: filterQuery,
          },
        }
      )

      const rows = await applyPendingToList(response.data.data, newPage === 1)
      if (newPage === 1) {
        setPackingDataList(rows)
      } else {
        // Drop anything already on screen. A row injected from the snapshot
        // after an offline scan will arrive again when its real page loads,
        // and the sort key (updatedAt) shifts as people scan, so a lazily
        // loaded page can legitimately overlap the one before it.
        setPackingDataList((prev) => {
          const seen = new Set(prev.map((row) => row._id))
          return [...prev, ...rows.filter((row) => !seen.has(row._id))]
        })
      }
      setHasMore(response.data.page < response.data.totalPages)
    } catch (error) {
      console.error('Failed to fetch packing list data:', error)
      setPackingDataList([])
    } finally {
      setIsLoading(false)
    }
  }

  const observer = useRef()
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prevPage) => prevPage + 1)
        }
      })
      if (node) observer.current.observe(node)
    },
    [isLoading, hasMore]
  )

  const refreshPending = useCallback(async () => {
    try {
      setPending(await pendingCount(id))
    } catch (error) {
      console.error('Could not read the scan queue:', error)
    }
  }, [id])

  useEffect(() => {
    refreshPending()
  }, [refreshPending])

  // Ask once, on load, so the queue is exempt from automatic eviction long
  // before anyone starts scanning into it.
  useEffect(() => {
    requestPersistentStorage().then((granted) => {
      setStorageIsPersistent(granted)
      if (!granted) {
        console.warn(
          'Persistent storage was declined - queued scans could be evicted if the disk fills. Sync promptly.'
        )
      }
    })
  }, [])

  // Pulled once when offline mode is switched on - while there is still a
  // connection to pull it with. Only the loaded page of the table is in memory,
  // which is far too little to tell whether a Return is valid.
  useEffect(() => {
    if (!offlineMode || listSnapshot) return undefined
    let cancelled = false

    const loadCodes = async () => {
      try {
        const { data } = await axios.get(
          `/api/v1/user/packing-list/details/${id}/codes`
        )
        if (cancelled) return
        const snapshot = new Map()
        ;(data?.codes || []).forEach((row) => {
          if (row.itemCode) snapshot.set(String(row.itemCode).toLowerCase(), row)
          if (row.barCode) snapshot.set(String(row.barCode).toLowerCase(), row)
        })
        setListSnapshot(snapshot)
      } catch (error) {
        console.error('Could not load packing list codes:', error)
        // Left null deliberately. Without the full list we must not reject a
        // Return that may well be valid, so validation stays off and the
        // server has the final say at sync.
        toast.warning(
          'Could not load the item list - offline scans will be checked at sync instead.'
        )
      }
    }

    loadCodes()
    return () => {
      cancelled = true
    }
  }, [offlineMode, listSnapshot, id])

  // Deliberately no automatic sync. Offline mode means offline: nothing goes
  // to the network until the operator presses Sync.

  // Closing the tab with scans still queued would strand them until someone
  // reopens this event on this same machine.
  useEffect(() => {
    if (pending === 0) return undefined
    const warn = (event) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [pending])

  // Sends queued scans in chunks. Each chunk leaves the queue only once the
  // server has ruled on it, so a connection that dies halfway leaves the rest
  // queued rather than losing them.
  const syncNow = useCallback(
    async (showToast = false) => {
      if (syncingRef.current) return
      syncingRef.current = true
      setIsSyncing(true)

      let applied = 0
      const rejected = []

      try {
        for (;;) {
          const chunk = await peekChunk(id, SYNC_CHUNK)
          if (chunk.length === 0) break

          const { data } = await axios.post(
            '/api/v1/user/packing-list/sync-scans',
            {
              eventId: id,
              userId,
              ops: chunk.map(({ opId, code, action, scannedAt }) => ({
                opId,
                code,
                action,
                scannedAt,
              })),
            }
          )

          const results = data?.results || []
          // 'error' is the only transient verdict - those stay queued and are
          // retried. Everything else the server has decided on for good.
          const settled = results
            .filter((result) => result.status !== 'error')
            .map((result) => result.opId)

          applied += results.filter((r) => r.status === 'applied').length
          rejected.push(...results.filter((r) => r.status === 'not_found'))

          await removeOps(settled)
          // Nothing left the queue, so another pass would spin forever.
          if (settled.length === 0) break
        }

        if (applied > 0) {
          // The server owns the totals - re-read rather than trusting any
          // arithmetic done here.
          await fetchData(1)
          setLastUpdate(new Date())
        }
        if (showToast && applied > 0) {
          toast.success(`Synced ${applied} scan${applied === 1 ? '' : 's'}`)
        }
        if (rejected.length > 0) {
          toast.warning(
            `${rejected.length} scan(s) not on this list: ${rejected
              .map((r) => r.code)
              .join(', ')}`
          )
        }
      } catch (error) {
        console.error('Sync failed:', error)
        if (showToast) {
          toast.error('Sync failed - scans are still saved and will retry.')
        }
      } finally {
        syncingRef.current = false
        setIsSyncing(false)
        await refreshPending()
      }
    },
    [id, userId, refreshPending]
  )

  // Bumps the matching row and floats it to the top, so an offline scan looks
  // the same as an online one. The row stays flagged as pending until it has
  // actually reached the server.
  const applyScanToList = (code) => {
    const fromSnapshot = listSnapshot?.get(String(code).trim().toLowerCase())

    setPackingDataList((prev) => {
      const index = prev.findIndex((row) => codeMatchesRow(code, row))

      if (index !== -1) {
        const row = prev[index]
        return [
          {
            ...row,
            returnQty: (Number(row.returnQty) || 0) + 1,
            pendingScans: (row.pendingScans || 0) + 1,
          },
          ...prev.filter((_, i) => i !== index),
        ]
      }

      // The item is on the list but on a page that has not been lazy-loaded.
      // Pull it out of the snapshot and show it anyway - which page an item
      // happens to sit on should not decide whether a scan is visible.
      if (!fromSnapshot) return prev
      return [
        {
          ...fromSnapshot,
          returnQty: (Number(fromSnapshot.returnQty) || 0) + 1,
          pendingScans: 1,
        },
        ...prev,
      ]
    })
  }

  const queueScan = async (code) => {
    const onList = listSnapshot
      ? listSnapshot.has(String(code).trim().toLowerCase())
      : null

    // A Return only ever applies to something already taken out for this event.
    // Reject it now, while the operator still has the book in hand, instead of
    // queueing work the server is certain to refuse at sync time.
    if (onList === false) {
      toast.error(`${code} is not on this packing list - nothing to return`)
      return
    }

    try {
      await enqueueScan({ eventId: id, code, action: 'Return', userId })
      await refreshPending()
      applyScanToList(code)
    } catch (error) {
      console.error('Could not queue scan:', error)
      toast.error(`Could not save ${code} locally - re-scan it.`)
    }
  }

  const toggleEdit = (itemId, field, currentValue) => {
    if (editMode === itemId && editField === field) {
      setEditMode(null)
      setEditField(null)

      setPackingDataList((currentList) =>
        currentList.map((item) =>
          item._id === itemId
            ? {
                ...item,
                // ?? not || : an original value of 0 is valid and must not
                // fall through to the edited value.
                [field]: originalValues[`${itemId}-${field}`] ?? item[field],
              }
            : item
        )
      )
    } else {
      setEditMode(itemId)
      setEditField(field)
      setOriginalValues((prev) => ({
        ...prev,
        [`${itemId}-${field}`]: currentValue,
      }))
    }
  }

  const handleQtyChange = (itemId, value, type) => {
    setPackingDataList((currentList) =>
      currentList.map((item) =>
        item._id === itemId ? { ...item, [type]: value } : item
      )
    )
  }

  const saveQty = async (itemId, type) => {
    const item = packingDataList.find((item) => item._id === itemId)
    if (!item) return

    const originalValue = Number(
      String(originalValues[`${itemId}-${type}`]).trim()
    )
    const updatedValue = Number(String(item[type]).trim())

    if (originalValue === updatedValue) {
      setEditMode(null)
      setEditField(null)
      setFocusBarcodeInput(true)
      return
    }
    try {
      await axios.patch(`/api/v1/user/packing-list/update-item/${itemId}`, {
        [type]: updatedValue,
        userId,
      })
      setEditMode(null)
      setEditField(null)
      fetchData()
      setLastUpdate(new Date())
      setFocusBarcodeInput(true)
    } catch (error) {
      console.error(`Failed to update ${type}:`, error)
      // Put the row back to its stored value. Without this the typed number
      // stays on screen looking saved, which is how a failed edit ends up
      // being discovered only at reconciliation time.
      setPackingDataList((currentList) =>
        currentList.map((row) =>
          row._id === itemId
            ? {
                ...row,
                [type]: originalValues[`${itemId}-${type}`] ?? row[type],
              }
            : row
        )
      )
      setEditMode(null)
      setEditField(null)
      toast.error(`Not saved: ${item.itemCode} ${type} reverted.`)
    }
  }

  const scanResult = async (code) => {
    try {
      const { data } = await axios.post(
        '/api/v1/user/packing-list/scan-stock',
        { searchData: code, id, userId }
      )
      if (data.products.length === 0) {
        toast.info('No records found')
      } else {
        fetchData()
        setLastUpdate(new Date())
      }
    } catch (error) {
      // No response at all means a dead or timed-out link, and we cannot tell
      // whether the server applied it. Report this one for a re-scan and switch
      // to offline mode so the scans that follow are queued instead of lost.
      if (!error.response) {
        setOfflineMode(true)
        toast.error(
          `No connection - ${code} was NOT recorded. Re-scan it; offline mode is now on.`
        )
      } else {
        toast.error('Item not found in the list!')
      }
    }
  }

  const handleKeyPress = async (event) => {
    if (event.key !== 'Enter') return
    event.preventDefault()

    const code = searchData.trim()
    if (!code) return

    // Clear immediately rather than after the response. A barcode scanner types
    // straight into this box, so leaving the previous code in it while a slow
    // request finishes is how two barcodes end up concatenated into one.
    setSearchData('')
    setFocusBarcodeInput(true)

    if (offlineMode) {
      await queueScan(code)
      return
    }
    await scanResult(code)
  }

  const handleQtyKeyPress = (event, itemId, type) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      saveQty(itemId, type)
    }
  }

  const clearInput = () => {
    setSearchData('')
    setFocusBarcodeInput(true)
  }

  const getSearchContent = (value) => {
    setSearchData(value)
  }

  const handleFilterChange = (value) => {
    setFilterQuery(value)
  }

  const closeModal = (e) => {
    e.preventDefault()
    setVisibleLogModal(false)
  }

  const openLogModal = (itemId) => {
    const item = packingDataList.find((item) => item._id === itemId)
    if (item) {
      setCurrentItemId(itemId)
      setCurrentItemDetails({
        itemCode: item.itemCode,
        description: item.description,
      })
      setVisibleLogModal(true)
    }
  }

  return (
    <Layout title={'Packing List'}>
      {/* Sticky rather than fixed with a hard-coded height, matching the stock
          check pages: the toolbar can wrap on a narrow screen without covering
          the first rows of the table. z-index stays below antd's modal base of
          1000. */}
      <div
        className='shadow-sm border-bottom px-3 px-md-4 pt-3 pb-3 bg-white'
        style={{ position: 'sticky', top: '50px', zIndex: 900 }}
      >
        <div className='d-flex align-items-center flex-wrap gap-3 mb-3'>
          <nav aria-label='breadcrumb' className='me-auto'>
            <ol className='breadcrumb wms-breadcrumb mb-0'>
              <li className='breadcrumb-item'>
                <button
                  type='button'
                  className='btn btn-link p-0 text-decoration-none'
                  onClick={() => navigate('/user/packing-list')}
                >
                  Packing List
                </button>
              </li>
              <li className='breadcrumb-item active' aria-current='page'>
                <span className='wms-crumb-current'>Return</span>
              </li>
            </ol>
          </nav>

          <div className='d-flex'>
            <input
              type='text'
              className='form-control'
              style={{ width: '250px', maxWidth: '100%' }}
              placeholder='Search Item Code or Desc'
              onChange={(e) => handleFilterChange(e.target.value)}
              value={filterQuery}
            />
            <button
              className='btn btn-secondary'
              onClick={() => setFilterQuery('')}
            >
              Clear
            </button>
          </div>

          <button
            type='button'
            className='btn btn-info'
            onClick={() => fetchData(1)}
            aria-label='Refresh'
            title='Refresh'
          >
            <i className='bi bi-arrow-clockwise'></i>
          </button>
        </div>

        <div className='d-flex align-items-center flex-wrap gap-3'>
          <div className='d-flex flex-grow-1' style={{ minWidth: '240px' }}>
            <input
              type='text'
              className='form-control'
              value={searchData}
              placeholder='Scan / Enter Item Code or Barcode for Return'
              onChange={(e) => getSearchContent(e.target.value)}
              onKeyDown={handleKeyPress}
              style={{ flex: 1 }}
              ref={barcodeInputRef}
              autoFocus
            />
            <button
              type='button'
              className='btn btn-secondary'
              disabled={!searchData}
              onClick={clearInput}
            >
              Clear
            </button>
          </div>

          <div className='form-check form-switch mb-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              id='offlineModeToggle'
              checked={offlineMode}
              onChange={(e) => setOfflineMode(e.target.checked)}
            />
            <label className='form-check-label' htmlFor='offlineModeToggle'>
              Offline
              {offlineMode && !storageIsPersistent && (
                <i
                  className='bi bi-exclamation-triangle-fill ms-1'
                  style={{ color: '#ffc107' }}
                  title='This browser would not grant persistent storage, so queued scans could be evicted if the disk fills. Sync often.'
                ></i>
              )}
            </label>
          </div>

          {(offlineMode || pending > 0) && (
            <button
              type='button'
              className={pending > 0 ? 'btn btn-warning' : 'btn btn-secondary'}
              onClick={() => syncNow(true)}
              disabled={isSyncing || pending === 0}
              title={
                pending > 0
                  ? `${pending} scan(s) saved on this device, not yet in the database`
                  : 'Everything is synced'
              }
            >
              {isSyncing
                ? 'Syncing...'
                : pending > 0
                ? `Sync ${pending}`
                : 'Synced'}
            </button>
          )}
        </div>
      </div>

      <style>{`
        .wms-breadcrumb {
          --bs-breadcrumb-divider: '\\203A';
          font-size: 0.95rem;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item {
          display: flex;
          align-items: center;
        }
        .wms-breadcrumb .breadcrumb-item + .breadcrumb-item::before {
          color: #adb5bd;
          padding: 0 0.6rem;
          font-size: 1.1rem;
        }
        .wms-crumb-current {
          font-weight: 600;
        }
      `}</style>

      <div>
        <div className='table-responsive'>
          <UserPackingListTable
            packingDataList={packingDataList}
            lastElementRef={lastElementRef}
            editMode={editMode}
            editField={editField}
            handleQtyChange={handleQtyChange}
            saveQty={saveQty}
            handleQtyKeyPress={handleQtyKeyPress}
            toggleEdit={toggleEdit}
            openLogModal={openLogModal}
          />
        </div>
      </div>

      <Modal
        closable={false}
        onCancel={() => setVisibleLogModal(false)}
        open={visibleLogModal}
        width={'70%'}
        footer={[
          <Button key='cancel' onClick={() => setVisibleLogModal(false)}>
            Cancel
          </Button>,
        ]}
      >
        <QtyLog
          closeLogModal={closeModal}
          itemId={currentItemId}
          itemDetails={currentItemDetails}
          lastUpdate={lastUpdate}
        />
      </Modal>
    </Layout>
  )
}

export default UserPackingListDetails
